import { z } from 'zod';
import { paginationQuery, cents, id } from './common.js';
import { workStatus } from './work.js';

export const earningsWorkspace = z.enum(['professional', 'office']);
export type EarningsWorkspace = z.infer<typeof earningsWorkspace>;
const reportDay = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date in YYYY-MM-DD format')
  .refine((value) => {
    const time = Date.parse(`${value}T00:00:00Z`);
    return Number.isFinite(time) && new Date(time).toISOString().slice(0, 10) === value;
  }, 'Choose a valid calendar date');
const periodFields = { workspace: earningsWorkspace, from: reportDay, to: reportDay };
function checkPeriod(value: { from: string; to: string }, ctx: z.RefinementCtx) {
  if (value.to < value.from)
    ctx.addIssue({
      code: 'custom',
      message: 'End date must be on or after start date',
      path: ['to'],
    });
  if (
    (Date.parse(`${value.to}T00:00:00Z`) - Date.parse(`${value.from}T00:00:00Z`)) / 86400000 >=
    366
  )
    ctx.addIssue({ code: 'custom', message: 'Choose a period of at most 366 days', path: ['to'] });
}
export const earningsPeriodQuery = z.object(periodFields).strict().superRefine(checkPeriod);
export const earningsReportQuery = z
  .object({ ...periodFields, ...paginationQuery.shape })
  .strict()
  .superRefine(checkPeriod);
export type EarningsPeriodQuery = z.infer<typeof earningsPeriodQuery>;
export type EarningsReportQuery = z.infer<typeof earningsReportQuery>;
export const earningsPaymentStatus = z.enum([
  'awaiting_hours',
  'under_review',
  'needs_correction',
  'approved_without_earning',
  'accrued',
  'payable',
  'included_in_payroll',
  'paid',
]);
export type EarningsPaymentStatus = z.infer<typeof earningsPaymentStatus>;
export const earningsReportRow = z.object({
  bookingId: id,
  shiftId: id,
  locationId: id,
  locationName: z.string(),
  professionalName: z.string(),
  startsAt: z.string(),
  endsAt: z.string(),
  timezone: z.string(),
  workStatus,
  paymentStatus: earningsPaymentStatus,
  earningId: id.nullable(),
  grossCents: cents.nullable(),
  netCents: cents.nullable(),
  officeFeeCents: cents.nullable(),
  officeTotalCents: cents.nullable(),
  approvedMinutes: z.number().int().positive().nullable(),
  hourlyRateCents: cents.nullable(),
  legacyEvidence: z.boolean(),
});
export type EarningsReportRow = z.infer<typeof earningsReportRow>;
export const earningsSummary = z.object({
  totalBookings: z.number().int().nonnegative(),
  awaitingHoursCount: z.number().int().nonnegative(),
  underReviewCount: z.number().int().nonnegative(),
  needsCorrectionCount: z.number().int().nonnegative(),
  approvedWithoutEarningCount: z.number().int().nonnegative(),
  recordedEarningCount: z.number().int().nonnegative(),
  recordedGrossCents: cents,
  recordedNetCents: cents,
  accruedGrossCents: cents,
  payableGrossCents: cents,
  payrollGrossCents: cents,
  paidGrossCents: cents,
  officeFeeCents: cents.nullable(),
  officeTotalCents: cents.nullable(),
  unknownOfficeTotalCount: z.number().int().nonnegative(),
  legacyEarningCount: z.number().int().nonnegative(),
});
export type EarningsSummary = z.infer<typeof earningsSummary>;
export const earningsReport = z.object({
  workspace: earningsWorkspace,
  from: reportDay,
  to: reportDay,
  dateBasis: z.literal('shift_start_utc'),
  generatedAt: z.string(),
  summary: earningsSummary,
  items: z.array(earningsReportRow),
  page: z.number().int().positive(),
  pageSize: z.number().int().min(1).max(100),
  total: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative(),
});
export type EarningsReport = z.infer<typeof earningsReport>;
export const earningsStatement = earningsReport
  .omit({ page: true, pageSize: true, totalPages: true })
  .extend({ complete: z.literal(true), rowLimit: z.number().int().positive() });
export type EarningsStatement = z.infer<typeof earningsStatement>;
